import { Button, Checkbox } from '@calizahq/react-components-v2'
import debounce from 'debounce-promise'
import { Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

import { DateTimeInputControlled, InputSelect } from '@/components'
import jobsMutations from '@/graphql/mutations/jobs.mutations'
import { UserQueries } from '@/graphql/queries/user.queries'
import { graphQLClient, useGraphqlMutation } from '@/hooks/useGraphql'
import useJobTemplates from '@/hooks/useJobs/useJobTemplates'
import { Error } from '@/views'

import DescriptionField from './DescriptionField'

type Props = {
  homeId?: number | null
  onSuccess?: () => void
  propertyId?: number | null
  reservationId?: number | null
}

const searchHosts = (inputValue: string) => {
  return graphQLClient.request<any>(UserQueries.getHostsBySearch(), { searchString: inputValue }).then((res) => {
    return res.hostSelectionUsers.map((host) => ({ label: host?.fullName, value: host.id }))
  })
}
const debouncedSearchHosts = debounce(searchHosts, 350, { leading: true })

export const CreateJobForm = ({ reservationId = null, homeId = null, propertyId = null, onSuccess }: Props) => {
  const {
    mutate,
    isPending: isLoading,
    isError,
  } = useGraphqlMutation(jobsMutations.createJob, {
    onSuccess: () => {
      onSuccess && onSuccess()
    },
  })

  const handleSubmit = (values: CreateJobFormValues) => {
    let job = {
      jobTemplateId: values?.jobTemplate,
      startsAt: values?.startsAt,
      hostId: values?.host,
      accessReviewed: values?.accessReviewed,
      description: values?.description,
      ...(!!reservationId && { reservationId }),
      ...(!!homeId && { homeId }),
      ...(!!propertyId && { propertyId }),
    }
    mutate({ job })
  }

  const jobTemplatesQuery = useJobTemplates()
  const jobTemplateOptions = jobTemplatesQuery?.data?.jobTemplates?.map((template) => ({
    label: template?.name || template?.displayName,
    value: template?.id,
  }))

  const loadHostOptions = (inputValue) => debouncedSearchHosts(inputValue)

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {() => {
        return (
          <Form className="flex w-full flex-row flex-wrap">
            <InputSelect
              className="mb-4 w-full"
              id="jobTemplate"
              isLoading={jobTemplatesQuery?.isLoading}
              label="Job Template"
              name="jobTemplate"
              options={jobTemplateOptions}
            />
            <DescriptionField className="mb-4 w-full" label="Description" name="description" />
            <DateTimeInputControlled className="mb-4 w-full" label="Starts At" name="startsAt" />
            <InputSelect
              async={true}
              className="mb-4 w-full"
              id="host"
              label="Host"
              loadOptions={loadHostOptions}
              name="host"
            />
            <Checkbox className="w-full pb-14" id="accessReviewed" label="Access Reviewed" name="accessReviewed" />

            {isError && <Error />}
            <Button
              className=" w-full bg-text-semantic-success"
              disabled={isLoading}
              isLoading={isLoading}
              type="submit"
            >
              Submit
            </Button>
          </Form>
        )
      }}
    </Formik>
  )
}

export type CreateJobFormValues = {
  accessReviewed: boolean
  description: string
  host: number | null
  jobTemplate: number | null
  startsAt: string | null
}

const initialValues = {
  jobTemplate: null,
  description: '',
  startsAt: null,
  host: null,
  accessReviewed: false,
}

const validationSchema = Yup.object({
  jobTemplate: Yup.number().nullable().required('Job template required'),
  description: Yup.string().nullable().required('Description required'),
  startsAt: Yup.string().nullable().required('Starts at required'),
  host: Yup.number().nullable(),
  accessReviewed: Yup.bool().oneOf(
    [true],
    'Please review the access instructions and verify them as correct before creating this job.',
  ),
})
